package com.zi.support.web

import com.zi.support.shared.AppInstalls
import com.zi.support.shared.DeviceHistoryEntry
import com.zi.support.shared.DevicePopup
import com.zi.support.shared.DeviceShape
import com.zi.support.shared.DeviceShapes
import com.zi.support.shared.DeviceStatus
import com.zi.support.shared.SupportDevice
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLElement

/** More > Devices > By app: one tab per app; each lists its servers, then a server's devices, when it was last opened there and the version it runs. */
internal object AppsScreen {
    fun show() {
        val tabs = AppInstalls.apps.joinToString("") { "<button role=\"tab\" data-app=\"${escape(it)}\">${escape(it)}</button>" }
        element("content")?.innerHTML = "$MORE_BACK<div class=\"page-intro\"><div><h2>Devices</h2><p>Each app's servers, the installs on each one, and their versions.</p></div></div>" +
            DeviceManagementScreen.viewSwitch(byPhone = false) +
            "<div id=\"apps-tabs\" class=\"segmented apps-tabs\" role=\"tablist\" aria-label=\"App\">$tabs</div>" +
            "<p id=\"apps-summary\" class=\"devices-summary\">Loading…</p><div id=\"apps-list\" class=\"devices-list\"></div>"
        bindMoreBack()
        DeviceManagementScreen.bindViewSwitch()
        var selected = AppInstalls.SUPPORT_APP
        var devices = emptyList<SupportDevice>()
        var server: String? = null
        val stops = mutableListOf<dynamic>()
        var timer = 0
        fun render() {
            // No leave hook: once the page is replaced, stop listening.
            val list = element("apps-list") ?: run { stops.forEach { it() }; window.clearInterval(timer); return }
            val buttons = element("apps-tabs")!!.querySelectorAll("button")
            for (i in 0 until buttons.length) (buttons.item(i) as HTMLElement).let {
                val on = it.getAttribute("data-app") == selected
                it.classList.toggle("active", on); it.setAttribute("aria-selected", on.toString())
            }
            val now = (js("Date.now()") as Double).toLong()
            if (AppInstalls.hasServers(selected) && server == null) {
                val groups = AppInstalls.servers(devices)
                element("apps-summary")?.textContent = "${devices.size} devices · ${groups.size} servers"
                list.innerHTML = groups.withIndex().joinToString("") { (index, group) ->
                    "<button type=\"button\" class=\"device-row device-open\" style=\"--i:$index\" data-server=\"${escape(group.label)}\">" +
                        "<span class=\"device-main\"><strong>${escape(group.label)}</strong><small>${group.devices.size} devices</small></span>" +
                        "<span class=\"device-state\"><span class=\"material-symbols-rounded\">chevron_right</span></span></button>"
                }
                val rows = list.querySelectorAll("[data-server]")
                for (i in 0 until rows.length) (rows.item(i) as HTMLElement).let { button ->
                    button.addEventListener("click", { server = button.getAttribute("data-server"); render() })
                }
                return
            }
            val shown = server?.let { label -> devices.filter { AppInstalls.serverOf(it) == label } } ?: devices
            element("apps-summary")?.textContent = (server?.let { "$it · " } ?: "") + "${shown.size} devices · most recently opened first"
            val sorted = AppInstalls.sorted(shown)
            val centre = AppInstalls.notificationCentreKey(selected) != null
            list.innerHTML = sorted.withIndex().joinToString("") { (index, device) ->
                val outdated = AppInstalls.isOutdated(device, devices)
                val number = if (device.number.isBlank()) "" else "<span class=\"device-number\">${escape(device.number)}</span>"
                val state = if (!centre || device.active) "" else " · Logged out"
                val row = "<span class=\"device-main\"><strong>$number${escape(device.deviceId.ifBlank { "No ID yet" })}</strong><small>${escape(DeviceStatus.subtitle(device) + state)}</small></span>" +
                    "<span class=\"device-state\"><strong class=\"app-version${if (outdated) " is-outdated" else ""}\">${escape(AppInstalls.versionLabel(device))}${if (outdated) " · update" else ""}</strong>" +
                    "<small class=\"opened-age\" style=\"color:${AppInstalls.freshness(device.lastOpenedMillis, now).hex}\"><i style=\"background:${AppInstalls.freshness(device.lastOpenedMillis, now).hex}\"></i>${escape(AppInstalls.openedLabel(device, now))}</small></span>"
                // Every app: click a row for the device's popup (3D model, details, history, every field).
                "<button type=\"button\" class=\"device-row device-open${if (centre && !device.active) " is-inactive" else ""}\" style=\"--i:$index\" data-device=\"$index\">$row</button>"
            }
            if (server != null) {
                list.insertAdjacentHTML("afterbegin", "<button type=\"button\" class=\"device-row device-open\" id=\"apps-back\"><span class=\"device-main\"><strong><span class=\"material-symbols-rounded\">arrow_back</span> All servers</strong></span></button>")
                element("apps-back")!!.addEventListener("click", { server = null; render() })
            }
            val rows = list.querySelectorAll("[data-device]")
            for (i in 0 until rows.length) (rows.item(i) as HTMLElement).let { button ->
                button.addEventListener("click", { sorted.getOrNull(button.getAttribute("data-device")!!.toInt())?.let { openDevice(selected, it, devices) } })
            }
        }
        // Support App and Driver Hub: one listener per collection, merged into one list. NotificationCentre apps: one listAppDevices call.
        fun listen() {
            stops.forEach { it() }; stops.clear()
            val app = selected
            server = null
            val byPath = mutableMapOf<String, List<SupportDevice>>()
            devices = emptyList(); render()
            AppInstalls.notificationCentreKey(app)?.let { key ->
                promise(FirebaseGateway.callable("listAppDevices", json("app" to key)), { result ->
                    if (app != selected) return@promise
                    val rows: Array<dynamic> = result.data.devices
                    devices = rows.map { row ->
                        val fields: dynamic = row.fields
                        val keys = js("Object.keys")(fields) as Array<String>
                        val history: Array<dynamic> = row.history ?: emptyArray()
                        AppInstalls.centreDevice(app, row.path as String, keys.associateWith { fields[it] as String },
                            (row.updatedAt as Number).toLong(), (row.lastOpenedAt as Number).toLong(),
                            (row.number as? String).orEmpty(), row.active != false,
                            history.map { DeviceHistoryEntry((it.at as Number).toLong(), it.text as String) })
                    }
                    render()
                }, { message -> if (app == selected) element("apps-summary")?.textContent = message })
                return
            }
            val fs = FirebaseGateway.firestore
            AppInstalls.collectionPaths(app).forEach { path ->
                val collection = FirebaseGateway.collection(path)
                val query = if (AppInstalls.activeOnly(app)) fs.query(collection, fs.where("active", "==", true)) else fs.query(collection)
                stops.add(FirebaseGateway.listenQuery(query, { rows ->
                    if (app == selected) {
                        byPath[path] = rows.map { row -> AppInstalls.device(app, path, { field(row.data, it) }, { timestampMillis(row.data[it]).toLong() }, allFields(row.data)) }
                        devices = byPath.values.flatten()
                        render()
                    }
                }, { message -> element("apps-summary")?.textContent = message }))
            }
        }
        val buttons = element("apps-tabs")!!.querySelectorAll("button")
        for (i in 0 until buttons.length) (buttons.item(i) as HTMLElement).let { button ->
            button.addEventListener("click", {
                val app = button.getAttribute("data-app").orEmpty()
                if (app != selected) { selected = app; listen() }
            })
        }
        listen()
        // Re-render each minute so "Opened 5 min ago" stays current.
        timer = window.setInterval({ render() }, 60_000)
    }

    /** Every field of a record as text, for the popup's technical details. */
    private fun allFields(data: dynamic): Map<String, String> {
        val keys = js("Object.keys")(data) as Array<String>
        return keys.associateWith { key ->
            val value = data[key]
            when {
                value == null -> ""
                js("typeof value.toDate === 'function'") as Boolean -> (value.toDate().toLocaleString("sv-SE", js("({timeZone:'Africa/Johannesburg'})")) as String)
                js("typeof value === 'object'") as Boolean -> JSON.stringify(value)
                else -> value.toString()
            }
        }
    }

    /** One device: its 3D model, state, the tiles, connection, history and every raw field. Same layout as Android and iOS. */
    internal fun openDevice(app: String, device: SupportDevice, devices: List<SupportDevice>) {
        val now = (js("Date.now()") as Double).toLong()
        val popup = DevicePopup.of(app, device, devices, now)
        val tiles = popup.tiles.withIndex().joinToString("") { (i, t) ->
            "<div class=\"dp-tile${if (t.flag) " is-flag" else ""}\" style=\"--i:$i\"><span>${escape(t.label)}</span><strong>${escape(t.value)}</strong></div>"
        }
        val rows = popup.rows.joinToString("") { "<div><dt>${escape(it.label)}</dt><dd>${escape(it.value)}</dd></div>" }
        val history = if (popup.history.isEmpty()) "<p class=\"dp-empty\">No history yet. It starts the next time the app is opened on this device.</p>"
            else "<ol class=\"dp-timeline\">" + popup.history.withIndex().joinToString("") { (i, h) ->
                "<li style=\"--i:$i\"><strong>${escape(h.text)}</strong><time>${escape(historyTime(h))}</time></li>" } + "</ol>"
        val fields = popup.fields.joinToString("") { "<div><dt>${escape(it.label)}</dt><dd>${escape(it.value)}</dd></div>" }
        modal(popup.title, """
            <div class="dp">
              <p class="dp-sub">${escape(popup.subtitle)}</p>
              <div class="dp-hero">${model(popup.shape, app)}<span class="dp-chip${if (popup.stateOn) " is-on" else ""}"><i></i>${escape(popup.state)}</span><span class="dp-hint"><span class="material-symbols-rounded">3d_rotation</span>Drag to rotate</span></div>
              <div class="dp-tiles">$tiles</div>
              <section class="dp-card"><h3>Connection</h3><dl class="dp-rows">$rows</dl></section>
              ${if (popup.hasHistory) "<section class=\"dp-card\"><h3>History</h3>$history</section>" else ""}
              <details class="dp-card dp-raw"><summary><h3>Technical details</h3><span>${popup.fields.size} fields</span><span class="material-symbols-rounded">expand_more</span></summary><dl class="dp-rows mono">$fields</dl></details>
            </div>
        """.trimIndent(), wide = true, layered = true)
        spin()
    }

    /** The device at its real proportions: glass front with the screen and front camera, back with its cameras, frame and buttons. */
    private fun model(shape: DeviceShape, app: String): String {
        val scale = 240f / shape.heightMm
        fun px(mm: Float) = "${(mm * scale * 10).toInt() / 10.0}px"
        val back = (shape.island?.let { "<i class=\"island\" style=\"left:${px(it.xMm)};top:${px(it.yMm)};width:${px(it.widthMm)};height:${px(it.heightMm)};border-radius:${px(it.radiusMm)};background:${DeviceShapes.hex(it.color)}\"></i>" } ?: "") +
            shape.lenses.joinToString("") { "<i class=\"lens\" style=\"left:${px(it.xMm - it.radiusMm)};top:${px(it.yMm - it.radiusMm)};width:${px(it.radiusMm * 2)};height:${px(it.radiusMm * 2)}\"></i>" }
        // Buttons on the right edge seen from the front; that face starts one corner radius down.
        val buttons = shape.buttons.joinToString("") { (top, length) -> "<i class=\"button\" style=\"top:calc(${px(top)} - var(--r));height:${px(length)}\"></i>" }
        val camera = if (shape.cameraInBezel) "<i class=\"front-cam tablet\" style=\"top:${px(shape.bezelTopMm / 2 - 0.8f)}\"></i>"
            else "<i class=\"front-cam\" style=\"top:${px(shape.bezelTopMm + 4f)};width:${px(shape.frontCameraMm * 2)};height:${px(shape.frontCameraMm * 2)}\"></i>"
        // Rugged corner guards on the front and back, left-edge keys and the scanner window, all in the accent colour.
        val guards = if (shape.bumperMm <= 0f) "" else listOf("tl", "tr", "bl", "br").joinToString("") { "<i class=\"guard $it\"></i>" }
        val leftKeys = shape.leftButtons.joinToString("") { (top, length) -> "<i class=\"button key\" style=\"top:calc(${px(top)} - var(--r));height:${px(length)}\"></i>" }
        val scanner = if (shape.scannerMm <= 0f) "" else "<i class=\"scanner\" style=\"width:${px(shape.scannerMm)}\"></i>"
        return """<div class="device-stage" aria-label="3D model of ${escape(shape.name)}"><div class="device-box" style="--w:${px(shape.widthMm)};--h:${px(shape.heightMm)};--d:${px(shape.depthMm)};--r:${px(shape.cornerMm)};--bezel:${px(shape.bezelMm)};--bezel-top:${px(shape.bezelTopMm)};--bezel-bottom:${px(shape.bezelBottomMm)};--guard:${px(shape.cornerMm + shape.bumperMm * 7f)};--accent:${DeviceShapes.hex(shape.accent ?: shape.frame)};--body:${DeviceShapes.hex(shape.body)};--frame:${DeviceShapes.hex(shape.frame)}">""" +
            """<div class="face front"><span class="screen"><b>${escape(app)}</b></span>$camera$guards</div><div class="face back">$back$guards<span class="brand">${escape(shape.name)}</span></div>""" +
            """<div class="face side left">$leftKeys</div><div class="face side right">$buttons</div><div class="face end top">$scanner</div><div class="face end bottom"></div></div></div>"""
    }

    private fun historyTime(entry: DeviceHistoryEntry): String =
        if (entry.atMillis <= 0) "" else kotlin.js.Date(entry.atMillis.toDouble()).asDynamic().toLocaleString(undefined, js("({dateStyle:'medium',timeStyle:'short',timeZone:'Africa/Johannesburg'})")) as String

    /** Turns slowly on its own; drag to turn it yourself. Stops when the popup closes. */
    private fun spin() {
        val stages = document.querySelectorAll(".device-stage")
        val stage = stages.item(stages.length - 1) as? HTMLElement ?: return
        val box = stage.querySelector(".device-box") as HTMLElement
        var angleY = -115.0; var angleX = -8.0; var intro = true; var dragging = false; var lastX = 0.0; var lastY = 0.0
        stage.addEventListener("pointerdown", { e -> dragging = true; intro = false; lastX = e.asDynamic().clientX as Double; lastY = e.asDynamic().clientY as Double; stage.asDynamic().setPointerCapture(e.asDynamic().pointerId) })
        stage.addEventListener("pointermove", { e ->
            if (dragging) {
                val x = e.asDynamic().clientX as Double; val y = e.asDynamic().clientY as Double
                angleY += (x - lastX) * 0.6; angleX = (angleX - (y - lastY) * 0.4).coerceIn(-60.0, 60.0); lastX = x; lastY = y
            }
        })
        stage.addEventListener("pointerup", { dragging = false })
        stage.addEventListener("pointercancel", { dragging = false })
        fun frame() {
            if (!stage.isConnected) return
            // Opens side-on and turns to face you, then keeps turning slowly.
            if (!dragging) { if (intro) { angleY += (-25.0 - angleY) * 0.07; if (angleY > -26.0) intro = false } else angleY += 0.25 }
            box.style.transform = "rotateX(${angleX}deg) rotateY(${angleY}deg)"
            window.requestAnimationFrame { frame() }
        }
        frame()
    }
}
